// @flow
import { intersection } from 'lodash';
import { find, map, filter, clone, uniq, remove } from 'lodash';
import { AwardSelector } from 'modules/awards/selector';
import { CmsDetailsSelector } from 'modules/cmsDetails/selector';
import { CmsPagesSelector } from 'modules/cmsPages/selector';
import { HomeSelector } from 'modules/home/selector';
import { ProjectBrowserSelector } from 'modules/projectBrowser/selector';
import type { AppState } from 'modules/rootReducer';
import type { GalleryImageType } from 'types/common';
import type {
  ActiveProjectFilters,
  LocationOption,
  ProjectFilters,
} from 'types/filter';
import type {
  Neighbourhood,
  ProjectAnnouncement,
  ProjectDetails,
} from 'types/project';
import { ProjectMapper } from 'mappers/project';

const getAnnouncements = (
  state: AppState
): Array<ProjectAnnouncement> | null => {
  return state.project.announcements;
};

const getAnnouncementsLoading = (state: AppState): boolean => {
  return state.project.loading.announcements;
};

const getAnnouncementsError = (state: AppState): string | null => {
  return state.project.error.announcements;
};

const getGallery = (state: AppState): Array<GalleryImageType> | null => {
  return state.project.gallery;
};

const getGalleryLoading = (state: AppState): boolean => {
  return state.project.loading.gallery;
};

const getGalleryError = (state: AppState): string | null => {
  return state.project.error.gallery;
};

const getProjects = (state: AppState): Object<ProjectDetails> | null => {
  return state.project.projects;
};

const getFilteredProjects = (
  state: AppState,
  productId: number | null
): Object<ProjectDetails> | null => {
  if (!state.project.projects) {
    return null;
  }

  let { list: projects, countries } = state.project.projects;
  if (productId) {
    projects = projects.filter(project => {
      const productIds = project.products.map(product => product.id);

      return productIds.indexOf(productId) > -1;
    });
  }

  const activeFilters = getActiveProjectFilters(state);
  if (!activeFilters) {
    return { list: projects, countries };
  }

  if (activeFilters.location && activeFilters.location.length > 0) {
    projects = projects.filter(project => {
      return activeFilters.location.indexOf(project.cityId) > -1;
    });
  }

  if (activeFilters.projectStatus && activeFilters.projectStatus.length > 0) {
    projects = projects.filter(project => {
      return activeFilters.projectStatus.indexOf(project.statusId) > -1;
    });
  }

  if (activeFilters.propertyType && activeFilters.propertyType.length > 0) {
    projects = projects.filter(project => {
      const propertyTypes = project.products.map(
        product => product.propertyType
      );

      return intersection(activeFilters.propertyType, propertyTypes).length > 0;
    });
  }

  if (activeFilters.beds) {
    projects = projects.filter(project => {
      const productBedrooms = project.products.map(product => product.bedRooms);
      const maxBedrooms = Math.max(...productBedrooms);

      return maxBedrooms >= activeFilters.beds;
    });
  }

  if (activeFilters.year && activeFilters.year.length > 0) {
    const minYear = activeFilters.year[0];
    const maxYear = activeFilters.year[1];
    projects = projects.filter(project => {
      return (
        project.yearOfCompletion >= minYear &&
        project.yearOfCompletion <= maxYear
      );
    });
  }

  if (activeFilters.size && activeFilters.size.length > 0) {
    const minSize = activeFilters.size[0];
    const maxSize = activeFilters.size[1];
    projects = projects.filter(project => {
      return (
        project.projectSizeId >= minSize && project.projectSizeId <= maxSize
      );
    });
  }

  return { list: projects, countries };
};

const getProjectFilters = (state: AppState): Object<ProjectFilters> | null => {
  return state.project.filters;
};

const getActiveProjectFilters = (
  state: AppState
): Object<ActiveProjectFilters> | null => {
  return state.project.activeFilters;
};

const getActiveProjectFilterNames = (state: AppState): Array<string> => {
  const filterNames = [];
  const projectFilters: ProjectFilters = getProjectFilters(state);
  const activeProjectFilters: ActiveProjectFilters = getActiveProjectFilters(
    state
  );
  if (!projectFilters || !activeProjectFilters) {
    return filterNames;
  }

  if (activeProjectFilters.beds) {
    filterNames.push(`${activeProjectFilters.beds}+ Beds`);
  }

  if (activeProjectFilters.propertyType.length > 0) {
    const propertyTypes = projectFilters.propertyType
      .filter(type => {
        return activeProjectFilters.propertyType.indexOf(type.id) > -1;
      })
      .map(type => type.title);
    filterNames.push(...propertyTypes);
  }

  if (activeProjectFilters.projectStatus.length > 0) {
    const statuses = projectFilters.projectStatus
      .filter(type => {
        return activeProjectFilters.projectStatus.indexOf(type.id) > -1;
      })
      .map(type => type.title);
    filterNames.push(...statuses);
  }

  if (activeProjectFilters.location.length > 0) {
    const cities = projectFilters.location.reduce(
      (activeCities: Array<string>, country: LocationOption) => {
        country.cities.forEach(city => {
          if (activeProjectFilters.location.indexOf(city.id) > -1) {
            activeCities.push(city.name);
          }
        });

        return activeCities;
      },
      []
    );

    filterNames.push(...cities);
  }

  if (activeProjectFilters.area && activeProjectFilters.area.length > 0) {
    const [minArea, maxArea] = activeProjectFilters.area;

    filterNames.push(
      `${minArea.toLocaleString()} - ${maxArea.toLocaleString()} Sq. Feet`
    );
  }

  return filterNames;
};

const isProjectsFiltered = (state: AppState): boolean => {
  const activeFilters: ActiveProjectFilters = getActiveProjectFilters(state);
  if (!activeFilters) {
    return false;
  }

  if (
    activeFilters.location.length > 0 ||
    activeFilters.propertyType.length > 0 ||
    activeFilters.projectStatus.length > 0 ||
    activeFilters.year.length > 0 ||
    activeFilters.size.length > 0 ||
    activeFilters.area.length > 0 ||
    activeFilters.beds
  ) {
    return true;
  }

  return false;
};

const getProjectLoading = (state: AppState): boolean => {
  return state.project.loading.projects;
};

const getProjectError = (state: AppState): string | null => {
  return state.project.error.projects;
};

const isDetailsPageLoading = (state: AppState): boolean => {
  return (
    ProjectBrowserSelector.isLoading(state) ||
    AwardSelector.getAwardLoading(state) ||
    HomeSelector.getVerticalsLoading(state) ||
    HomeSelector.getSiteSettingsLoading(state) ||
    HomeSelector.getContactsLoading(state) ||
    CmsPagesSelector.isCmsPageLoading(state) ||
    CmsDetailsSelector.getCmsDetailsDataLoading(state) ||
    getGalleryLoading(state) ||
    getNeighborhoodGuideLoading(state) ||
    getProjectLoading(state) ||
    getConstructionGalleryLoading(state)
  );
};

const getNeighbourhoodGuide = (
  state: AppState
): Array<Neighbourhood> | null => {
  const data = state.project.neighbourhood;
  const projectDetails = state.projectBrowser.projectDetails;

  let neighbourhoodData = {};
  let neighbourhoodTypes = {};
  if (data && projectDetails) {
    neighbourhoodData.project = {
      latitude: projectDetails.project.latitude,
      longitude: projectDetails.project.longitude,
      name: projectDetails.project.title,
      location: projectDetails.project.area
        ? `${projectDetails.project.area}, ${projectDetails.project.city}`
        : `${projectDetails.project.city}, ${projectDetails.project.country}`,
    };
    neighbourhoodData.list = getProjectNeighbourhoods(
      data,
      projectDetails.project.id
    );
    neighbourhoodData.list.map(item => {
      if (!neighbourhoodTypes[item.neighbourhoodTypeId]) {
        neighbourhoodTypes[item.neighbourhoodTypeId] = find(
          ProjectMapper.neighbourhoodTypes,
          {
            id: item.neighbourhoodTypeId,
          }
        );
      }
    });
    neighbourhoodData.types = map(neighbourhoodTypes, val => val);
  }
  return data ? neighbourhoodData : null;
};

const getNeighborhoodGuideLoading = state => {
  return state.project.loading.neighbourhood;
};

const getNeighborhoodGuideError = state => {
  return state.project.error.neighbourhood;
};

const getProjectNeighbourhoods = (data, projectId) => {
  return filter(data, {
    projectId: projectId,
    status: 'A',
  });
};

const filteredNeighbourhoods = (state: AppState) => {
  const neighbourhoods = state.project.neighbourhood;
  const projectDetails = state.projectBrowser.projectDetails;
  if (!neighbourhoods) {
    return null;
  }
  const projectNeighbourhoods = getProjectNeighbourhoods(
    neighbourhoods,
    projectDetails.project.id
  );

  const activeNeighbourhoodFilter = state.project.activeNeighbourhoodFilter;
  if (!activeNeighbourhoodFilter) {
    return projectNeighbourhoods;
  }

  return filter(
    projectNeighbourhoods,
    ({ neighbourhoodTypeId }) =>
      neighbourhoodTypeId === activeNeighbourhoodFilter.id
  );
};

const neighbourhoodActiveFilter = (state: AppState) => {
  if (!state.project.activeNeighbourhoodFilter) {
    return null;
  }
  return state.project.activeNeighbourhoodFilter;
};

const getConstructionGallery = (state: AppState): null => {
  return state.project.constructionGallery;
};

const getConstructionGalleryLoading = (state: AppState): boolean => {
  return state.project.loading.constructionGallery;
};

const getConstructionGalleryError = (state: AppState): string | null => {
  return state.project.error.constructionGallery;
};

const getCompletedProjects = (state: AppState): null => {
  return state.project.completedProjects;
};

const getCompletedProjectsLoading = (state: AppState): boolean => {
  return state.project.loading.completedProjects;
};

const getCompletedProjectsError = (state: AppState): string | null => {
  return state.project.error.completedProjects;
};

const getProjectBrochure = (state: AppState): null => {
  return state.project.projectBrochure;
};

const getProjectBrochureLoading = (state: AppState): boolean => {
  return state.project.loading.projectBrochure;
};

const getProjectBrochureError = (state: AppState): string | null => {
  return state.project.error.projectBrochure;
};

export const ProjectSelector = {
  getAnnouncements,
  getAnnouncementsLoading,
  getAnnouncementsError,
  getGallery,
  getGalleryLoading,
  getGalleryError,
  isDetailsPageLoading,
  getProjects,
  getFilteredProjects,
  getProjectFilters,
  getActiveProjectFilters,
  getActiveProjectFilterNames,
  isProjectsFiltered,
  getProjectLoading,
  getProjectError,
  getNeighbourhoodGuide,
  getNeighborhoodGuideLoading,
  getNeighborhoodGuideError,
  filteredNeighbourhoods,
  neighbourhoodActiveFilter,
  getConstructionGallery,
  getConstructionGalleryLoading,
  getConstructionGalleryError,
  getCompletedProjects,
  getCompletedProjectsError,
  getCompletedProjectsLoading,
  getProjectBrochure,
  getProjectBrochureError,
  getProjectBrochureLoading,
};
